<template>

    <div class="form-group" :class="{ 'mb-4': !removeMargin }">

        <label :for="movementSelectId">
            <strong>
                {{ title }}
                <span v-if="required" class="text-danger">*</span>
            </strong>
        </label>

        <v-select
            v-model="model"
            :options="movements"
            :clearable="!isDisabled"
            placeholder="Select a Movement"
            label="formatted_label"
            @search="fetchOptions"
            :disabled="isDisabled"
            :id="movementSelectId"
        />

        <input type="hidden" name="movement_id" id="movement_id" :value="model?.id ?? ''">

        <div v-if="errors?.length > 0" >
            <div class="alert alert-danger mt-2" role="alert" v-for="(error, index) in errors" :key="index">
                {{ error }}
            </div>
        </div>

    </div>

</template>

<script setup>

/*
    A select component for selecting a Movement.

    A job is required.

    In order of precedence:

    If there is no movements passed in, get from the API

    It accepts a movement prop to set the initial value. If it's the only movement disable the select.
    If only one movement is available from passed in movements, it selects it and disables the select.
    It fetches movements from the API as the user types in the search box and sets to the movements.
 */

import axios from 'axios';
import debounce from 'lodash/debounce';
import VSelect from 'vue-select';
import { watch, ref, onMounted, useId } from "vue";

const props = defineProps({
    job: {
        type: Object,
        required: true
    },
    title: {
        type: String,
        default: 'Movement'
    },
    old: {
        type: String,
        required: false
    },
    errors: {
        type: Array,
        required: false
    },
    movement: {
        type: Object,
        default: () => ({})
    },
    disableWhenPassedCustomer: {
        type: Boolean,
        default: false
    },
    required: {
        type: Boolean,
        default: false
    },
    removeMargin: {
        type: Boolean,
        default: false
    }
})

const model = defineModel({
    type: Object,
    default: null
});

const emit = defineEmits(['update-movement'])

const isDisabled = ref(false);
const movements = ref([]);

const movementSelectId = useId();

const fetchOptions = (search, loading) => {
    // if (!search) {
    //     fetchCustomers(loading); // Refresh Movements
    //     return;
    // }
    if (search.length > 2) {
        debounceFetchOptions(search, loading);
    }
};

const debounceFetchOptions = debounce(async function (search, loading) {
    try {
        if (loading) loading(true);
        const url = `${generateAllMovementsRoute()}?search=${encodeURIComponent(search)}`;
        const { data } = await axios.get(url);
        movements.value = Array.isArray(data) ? data : Object.values(data);
    } catch (error) {
        console.error(error);
    } finally {
        if (loading) loading(false);
    }
}, 500);

const generateAllMovementsRoute = () => {
    return route('api.movements.movements.select', { job: props.job?.id });
}

onMounted(async () => {
    try {
        const { data } = await axios.get(generateAllMovementsRoute());
        movements.value = Array.isArray(data) ? data : Object.values(data);

        // If nothing is passed in, set the first customer as the Selected Customer
        if (!model.value && movements.value.length > 0) {
            model.value = movements.value[0]
        }

        if (props.movement) {
            model.value = movements.value.find(movement => movement?.id === props.movement?.id) || model.value
            isDisabled.value = true
        }

    } catch (error) {
        console.error(error);
    }
});

watch(model, (movement) => {
    emit('update-movement', movement)
});

</script>
